import { Box, Button, HStack, Text, VStack, useToast } from '@chakra-ui/react';
import { Input } from '@gamma/ui/form-fields';
import WarningSharpIcon from '@gamma/ui/icons/WarningSharpIcon';
import { Modal } from '@gamma/ui/overlay';
import { i18n } from '@investigator/shared/localization';
import { useForm } from 'react-hook-form';
import validator from 'validator';
import { useAddIdpDomain } from '@investigator/features/admin/queries/useAddIdpDomain';
import { GET_IDP_DOMAINS } from '@investigator/features/admin/queries/useGetIdpDomains';
import { MAX_DOMAIN_CHAR_LENGTH } from './constants';

const domainsText = i18n.pages.access.domainsList;

interface AddDomainModalProps {
  isOpen: boolean;
  onClose: () => void;
  existingDomains?: string[];
}

interface FormData {
  domain: string;
}

export const AddDomainModal = ({
  isOpen,
  onClose,
  existingDomains = [],
}: AddDomainModalProps) => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
    watch,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      domain: '',
    },
  });

  const domainValue = watch('domain');

  const [addIdpDomain, { loading: isSubmitting }] = useAddIdpDomain({
    onCompleted: () => {
      toast({
        status: 'success',
        title: domainsText.toasts.addDomain.success.title,
        description: i18n.formatString(
          domainsText.toasts.addDomain.success.description,
          domainValue,
        ),
        isClosable: true,
        position: 'bottom-right',
      });
      reset();
      onClose();
    },
    onError: () => {
      toast({
        status: 'error',
        title: domainsText.toasts.addDomain.error.title,
        description: i18n.formatString(
          domainsText.toasts.addDomain.error.description,
          domainValue,
        ),
        isClosable: true,
        position: 'bottom-right',
      });
    },
    refetchQueries: [GET_IDP_DOMAINS],
  });

  const onSubmit = (data: FormData) => {
    // Check for duplicates
    if (
      existingDomains
        .map((d) => d.toLowerCase())
        .includes(data.domain.toLowerCase())
    ) {
      setError('domain', {
        type: 'validation',
        message: domainsText.validation.exists,
      });
      return;
    }

    addIdpDomain({
      variables: { idp_domain: data.domain },
    });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!isSubmitting}
      closeOnEsc={!isSubmitting}
      title={domainsText.addDomain}
      data-testid="add-domain-modal"
      body={
        <VStack spacing={4} align="stretch">
          <Text color="text.secondary">{domainsText.addDomainSubheader}</Text>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Input
              {...register('domain', {
                required: domainsText.validation.required,
                validate: (value) => {
                  if (!value) return true; // Let required handle empty values
                  if (value.length > MAX_DOMAIN_CHAR_LENGTH) {
                    return domainsText.validation.maxLength;
                  }
                  return (
                    validator.isFQDN(value) ||
                    domainsText.validation.invalidFormat
                  );
                },
              })}
              placeholder={domainsText.domainPlaceholder}
              data-testid="domain-input"
              label={domainsText.domain}
              error={!!errors.domain}
            />
            {errors.domain && (
              <HStack marginTop={1} textColor="red.300">
                <WarningSharpIcon />
                <Text fontSize="sm">{errors.domain.message}</Text>
              </HStack>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
              <Button
                type="button"
                variant="solid"
                colorScheme="gray"
                onClick={handleClose}
                isDisabled={isSubmitting}
                data-testid="cancel-button"
              >
                {domainsText.cancel}
              </Button>
              <Button
                type="submit"
                variant="solid"
                colorScheme="blue"
                isDisabled={!domainValue || !isValid || isSubmitting}
                isLoading={isSubmitting}
                loadingText={domainsText.adding}
                data-testid="add-domain-submit-button"
              >
                {domainsText.addDomain}
              </Button>
            </Box>
          </form>
        </VStack>
      }
    />
  );
};
